import type { CommercialTermsProductVariant,
  CommissionProductVariant,
  FixedPerUnitProductVariant,
  OneTimeProductVariant,
  PricingModel, 
  RefundProductVariant } from '@weft/types';
import { PRICING_MODELS } from '@weft/types';
import type { FilterableProductVariant } from './product-variants-filter-types/filterable-product-variant';
import type { SubscriptionProductVariant } from './product-variants-filter-types/pricing-product-variant';
import type { InstallmentsProductVariant } from './product-variants-filter-types/pricing-product-variant/pricing-product-variants/fixed-product-variant/fixed-product-variants/installments-product-variant';
import type { UsageProductVariant } from './product-variants-filter-types/pricing-product-variant/pricing-product-variants/usage-product-variant';
import type { PerUnitProductVariant } from './product-variants-filter-types/pricing-product-variant/pricing-product-variants/usage-product-variant/usage-product-variants/per-unit-product-variant';
import type { TiersProductVariant } from './product-variants-filter-types/pricing-product-variant/pricing-product-variants/usage-product-variant/usage-product-variants/tiers-product-variant';

export function getTiersProductVariants(variants: FilterableProductVariant[]) {
  return variants.filter(isTiersProductVariant);
}

export function getPerUnitProductVariants(variants: FilterableProductVariant[]) {
  return variants.filter(isPerUnitProductVariant);
}

export function isSubscriptionProductVariant(variant: FilterableProductVariant): variant is SubscriptionProductVariant {
  return isVariantOfPricingModel(variant, PRICING_MODELS.Subscription);
}

export function isCommissionProductVariant(variant: FilterableProductVariant): variant is CommissionProductVariant {
  return isVariantOfPricingModel(variant, PRICING_MODELS.Commission);
}

export function isOneTimeProductVariant(variant: FilterableProductVariant): variant is OneTimeProductVariant {
  return isVariantOfPricingModel(variant, PRICING_MODELS.FixedOneTime);
}

export function isFixedPerUnitProductVariant(variant: FilterableProductVariant): variant is FixedPerUnitProductVariant {
  return isVariantOfPricingModel(variant, PRICING_MODELS.FixedPerUnit);
}

export function isCommercialTermsProductVariant(variant: FilterableProductVariant): variant is CommercialTermsProductVariant {
  return isVariantOfPricingModel(variant, PRICING_MODELS.CommercialTerms);
}

export function isInstallmentsProductVariant(variant: FilterableProductVariant): variant is InstallmentsProductVariant {
  return isVariantOfPricingModel(variant, PRICING_MODELS.Installments);
}

export function isRefundProductVariant(variant: FilterableProductVariant): variant is RefundProductVariant {
  return isVariantOfPricingModel(variant, PRICING_MODELS.Refund);
}

export function isUsageProductVariant(variant: FilterableProductVariant): variant is UsageProductVariant {
  return isTiersProductVariant(variant) || isPerUnitProductVariant(variant);
}

export function isTiersProductVariant(variant: FilterableProductVariant): variant is TiersProductVariant {
  return isVariantOfPricingModel(variant, PRICING_MODELS.Tiers);
}

export function isTieredSubscriptionProductVariant(variant: FilterableProductVariant): variant is TiersProductVariant {
  return isVariantOfPricingModel(variant, PRICING_MODELS.TieredSubscription);
}

export function isTiersVolumeProductVariant(variant: FilterableProductVariant): variant is TiersProductVariant {
  return isTiersProductVariant(variant) && variant.pricing.priceConfiguration.isVolume;
}

export function isPerUnitProductVariant(variant: FilterableProductVariant): variant is PerUnitProductVariant {
  return isVariantOfPricingModel(variant, PRICING_MODELS.PerUnit);
}

export function isVariantOfPricingModel(variant: FilterableProductVariant, pricingModel: PricingModel) {
  return variant.pricing.pricingModel === pricingModel;
}